import { z } from 'zod'

import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'
import type { SessionContext } from '@/Atomic/Widgets/AiChatBot/core/engine/types'
import { type ScrivitoAccessor } from '@/Atomic/Widgets/AiChatBot/core/tools/shared/entity-serializer'
import { DataClassName } from '@/Data/const'

export const listTeamLeadersTool = defineTool({
  name: 'list_team_leaders',
  description:
    'List all team leaders in time tracking. Returns each leader id and name. Use this to present a leader selection to the user before fetching team utilization.',
  inputSchema: () => z.object({}),
  concurrencySafe: true,

  async run(_args: object, _ctx: SessionContext) {
    const { data } = getHostRuntime()
    if (!data.resolveDataClass(DataClassName.TIMERECORD_TEAM)) {
      throw new Error('TimerecordTeam DataClass not found')
    }

    // Fetch all team records
    const allTeams = (await data.query(
      DataClassName.TIMERECORD_TEAM,
    )) as ScrivitoAccessor[]

    // Dedupe by leader field
    const leaderIds = Array.from(
      new Set(
        allTeams.map((item) => item.get('leader') as string).filter(Boolean),
      ),
    )

    // Resolve each leader to a TimerecordUser
    if (!data.resolveDataClass(DataClassName.TIMERECORD_USER)) {
      throw new Error('TimerecordUser DataClass not found')
    }

    const leaders = await Promise.all(
      leaderIds.map(async (id) => {
        const leader = (await data
          .get(DataClassName.TIMERECORD_USER, id)
          .catch(() => null)) as ScrivitoAccessor | null
        if (!leader) {
          return { _id: id, name: 'Unknown' }
        }
        return {
          _id: leader.id(),
          name: (leader.get('name') as string) ?? 'Unknown',
        }
      }),
    )

    return {
      leaders,
      _instruction:
        'Present the list of team leaders and ask the user to select one or more. Use the show_options tool with the returned leader names and IDs so the user can confirm their selection before fetching utilization data.',
    }
  },
})
